import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { MapContainer, TileLayer, Circle, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

const API = 'https://app.b3rastreamento.com.br/api';

const tiposAlerta = [
  { value: 'ignicao_ligada', label: '🔑 Ignição Ligada', descricao: 'Alerta quando a ignição for ligada' },
  { value: 'ignicao_desligada', label: '🔒 Ignição Desligada', descricao: 'Alerta quando a ignição for desligada' },
  { value: 'velocidade', label: '🚨 Velocidade Máxima', descricao: 'Alerta quando exceder a velocidade configurada' },
  { value: 'cerca_virtual', label: '📍 Cerca Virtual', descricao: 'Alerta quando entrar ou sair de uma área' },
  { value: 'sem_sinal', label: '📵 Sem Sinal', descricao: 'Alerta quando ficar sem sinal por X minutos' },
  { value: 'bateria_desconectada', label: '🔋 Corte de Energia', descricao: 'Alerta quando a bateria/fiação do equipamento for desconectada' },
];

const iconeCerca = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

// Componente auxiliar: captura cliques no mapa e avisa o componente pai
function MapaClicavel({ onClique }) {
  useMapEvents({
    click: (e) => {
      onClique(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function Alertas() {
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [aba, setAba] = useState('ocorrencias');
  const [alertas, setAlertas] = useState([]);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [targets, setTargets] = useState([]);
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [naoLidos, setNaoLidos] = useState(0);
  const [form, setForm] = useState({
    target_id: '', tipo: 'velocidade', nome: '', descricao: '',
    notificar_email: true, notificar_push: true, email_destino: '',
    configuracao: { velocidade_maxima: 80, minutos_sem_sinal: 30, lat: -23.3045, lng: -51.1696, raio: 500, tipo_cerca: 'saida' }
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    carregarDados();
    const interval = setInterval(carregarNaoLidos, 30000);
    return () => clearInterval(interval);
  }, []);

  const carregarDados = async () => {
    try {
      const [alertasRes, ocorrenciasRes, targetsRes] = await Promise.all([
        axios.get(`${API}/alertas`, { headers }),
        axios.get(`${API}/alertas/ocorrencias`, { headers }),
        axios.get(`${API}/targets`, { headers }),
      ]);
      setAlertas(alertasRes.data);
      setOcorrencias(ocorrenciasRes.data);
      setTargets(targetsRes.data);
      setNaoLidos(ocorrenciasRes.data.filter(o => !o.lido).length);
    } catch (err) { console.error(err); }
  };

  const carregarNaoLidos = async () => {
    try {
      const res = await axios.get(`${API}/alertas/nao-lidos`, { headers });
      setNaoLidos(res.data.total);
    } catch (err) { console.error(err); }
  };

  const salvar = async () => {
    if (!form.tipo || !form.nome) { alert('Tipo e nome são obrigatórios!'); return; }
    if (form.tipo === 'cerca_virtual' && (!form.configuracao.lat || !form.configuracao.lng)) {
      alert('Clique no mapa para definir o centro da cerca virtual!');
      return;
    }
    setSalvando(true);
    try {
      await axios.post(`${API}/alertas`, form, { headers });
      setModalAberto(false);
      carregarDados();
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao salvar'); }
    setSalvando(false);
  };

  const toggleAtivo = async (alerta) => {
    await axios.put(`${API}/alertas/${alerta.id}`, { ...alerta, ativo: !alerta.ativo }, { headers });
    carregarDados();
  };

  const deletar = async (id) => {
    if (!window.confirm('Deseja remover este alerta?')) return;
    await axios.delete(`${API}/alertas/${id}`, { headers });
    carregarDados();
  };

  const marcarTodosLidos = async () => {
    await axios.patch(`${API}/alertas/ocorrencias/todos/lido`, {}, { headers });
    carregarDados();
  };

  const marcarLido = async (id) => {
    await axios.patch(`${API}/alertas/ocorrencias/${id}/lido`, {}, { headers });
    carregarDados();
  };

  const tipoCor = (tipo) => {
    if (tipo === 'velocidade') return { bg: '#FCEBEB', cor: '#A32D2D', icon: '🚨' };
    if (tipo === 'ignicao_ligada') return { bg: '#E1F5EE', cor: '#0F6E56', icon: '🔑' };
    if (tipo === 'ignicao_desligada') return { bg: '#FAEEDA', cor: '#633806', icon: '🔒' };
    if (tipo === 'cerca_virtual') return { bg: '#E6F1FB', cor: '#185FA5', icon: '📍' };
    if (tipo === 'sem_sinal') return { bg: '#f0f0f0', cor: '#666', icon: '📵' };
    if (tipo === 'bateria_desconectada') return { bg: '#FCEBEB', cor: '#A32D2D', icon: '🔋' };
    return { bg: '#f0f0f0', cor: '#666', icon: '🔔' };
  };

  const formatarRaio = (raio) => {
    if (raio >= 1000) return `${(raio / 1000).toFixed(1)} km`;
    return `${raio} m`;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Sidebar aberta={sidebarAberta} setAberta={setSidebarAberta} />

      <div style={{ background: '#1D9E75', padding: '0 24px', height: '60px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 500, boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => setSidebarAberta(!sidebarAberta)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: 'white' }}>{sidebarAberta ? '✕' : '☰'}</button>
          <span style={{ color: 'white', fontWeight: '700', fontSize: '18px' }}>🔔 Alertas</span>
          {naoLidos > 0 && <span style={{ background: '#E24B4A', color: 'white', borderRadius: '12px', padding: '2px 8px', fontSize: '12px', fontWeight: '700' }}>{naoLidos}</span>}
        </div>
        {aba === 'configurar' && (
          <button onClick={() => { setForm({ target_id: '', tipo: 'velocidade', nome: '', descricao: '', notificar_email: true, notificar_push: true, email_destino: '', configuracao: { velocidade_maxima: 80, minutos_sem_sinal: 30, lat: -23.3045, lng: -51.1696, raio: 500, tipo_cerca: 'saida' } }); setModalAberto(true); }}
            style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
            + Novo Alerta
          </button>
        )}
      </div>

      <div style={{ marginTop: '60px', padding: '24px' }}>

        {/* Abas */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', background: 'white', borderRadius: '12px', padding: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          {[
            { key: 'ocorrencias', label: `🔔 Ocorrências ${naoLidos > 0 ? `(${naoLidos})` : ''}` },
            { key: 'configurar', label: '⚙️ Configurar Alertas' },
          ].map(a => (
            <button key={a.key} onClick={() => setAba(a.key)} style={{
              background: aba === a.key ? '#1D9E75' : 'transparent',
              color: aba === a.key ? 'white' : '#666',
              border: 'none', borderRadius: '8px', padding: '8px 16px',
              cursor: 'pointer', fontWeight: '600', fontSize: '13px'
            }}>{a.label}</button>
          ))}
        </div>

        {/* OCORRÊNCIAS */}
        {aba === 'ocorrencias' && (
          <div>
            {naoLidos > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                <button onClick={marcarTodosLidos} style={{ background: '#E6F1FB', color: '#185FA5', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                  ✅ Marcar todos como lidos
                </button>
              </div>
            )}
            {ocorrencias.length === 0 ? (
              <div style={{ background: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center', color: '#999', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔔</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>Nenhuma ocorrência registrada</div>
                <div style={{ fontSize: '13px', marginTop: '8px' }}>Configure alertas para monitorar seus veículos</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {ocorrencias.map((o, i) => {
                  const tc = tipoCor(o.tipo);
                  return (
                    <div key={i} style={{
                      background: o.lido ? 'white' : '#FAFFF9',
                      borderRadius: '12px', padding: '16px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                      borderLeft: `4px solid ${o.lido ? '#ddd' : tc.cor}`,
                      display: 'flex', alignItems: 'center', gap: '14px'
                    }}>
                      <div style={{ fontSize: '28px' }}>{tc.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{o.target_icone} {o.target_nome}</span>
                          <span style={{ background: tc.bg, color: tc.cor, padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500' }}>{tiposAlerta.find(t => t.value === o.tipo)?.label || o.tipo}</span>
                          {!o.lido && <span style={{ background: '#1D9E75', color: 'white', padding: '2px 6px', borderRadius: '6px', fontSize: '10px', fontWeight: '700' }}>NOVO</span>}
                        </div>
                        <div style={{ fontSize: '13px', color: '#555' }}>{o.mensagem}</div>
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>{new Date(o.criado_em).toLocaleString('pt-BR')}</div>
                      </div>
                      {!o.lido && (
                        <button onClick={() => marcarLido(o.id)} style={{ background: '#f0f0f0', color: '#666', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}>
                          Marcar lido
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* CONFIGURAR */}
        {aba === 'configurar' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {alertas.length === 0 ? (
              <div style={{ gridColumn: '1/-1', background: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center', color: '#999', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚙️</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>Nenhum alerta configurado</div>
                <div style={{ fontSize: '13px', marginTop: '8px' }}>Clique em "+ Novo Alerta" para começar</div>
              </div>
            ) : alertas.map((a, i) => {
              const tc = tipoCor(a.tipo);
              return (
                <div key={i} style={{ background: 'white', borderRadius: '12px', padding: '18px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: `3px solid ${a.ativo ? tc.cor : '#ddd'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>{tc.icon} {a.nome}</div>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>{a.target_icone} {a.target_nome || 'Todos os veículos'}</div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <div onClick={() => toggleAtivo(a)} style={{
                        width: '36px', height: '20px', borderRadius: '10px',
                        background: a.ativo ? '#1D9E75' : '#ccc',
                        position: 'relative', cursor: 'pointer', transition: 'background .2s'
                      }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'white', position: 'absolute', top: '2px', left: a.ativo ? '18px' : '2px', transition: 'left .2s' }} />
                      </div>
                    </label>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                    {tiposAlerta.find(t => t.value === a.tipo)?.descricao}
                    {a.configuracao?.velocidade_maxima && ` — ${a.configuracao.velocidade_maxima} km/h`}
                    {a.configuracao?.minutos_sem_sinal && ` — ${a.configuracao.minutos_sem_sinal} min`}
                    {a.configuracao?.raio && ` — raio de ${formatarRaio(a.configuracao.raio)}`}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {a.notificar_email && <span style={{ background: '#E6F1FB', color: '#185FA5', padding: '2px 8px', borderRadius: '6px', fontSize: '11px' }}>📧 Email</span>}
                    {a.notificar_push && <span style={{ background: '#E1F5EE', color: '#0F6E56', padding: '2px 8px', borderRadius: '6px', fontSize: '11px' }}>🔔 Push</span>}
                  </div>
                  <button onClick={() => deletar(a.id)} style={{ marginTop: '12px', background: '#FCEBEB', color: '#A32D2D', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', width: '100%' }}>
                    🗑️ Remover
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL NOVO ALERTA */}
      {modalAberto && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '600px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>🔔 Novo Alerta</h2>
              <button onClick={() => setModalAberto(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#999' }}>×</button>
            </div>
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

              <div>
                <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Tipo de Alerta *</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {tiposAlerta.map(t => (
                    <div key={t.value} onClick={() => setForm(prev => ({ ...prev, tipo: t.value, nome: t.label }))}
                      style={{ padding: '10px 14px', borderRadius: '8px', border: `2px solid ${form.tipo === t.value ? '#1D9E75' : '#eee'}`, cursor: 'pointer', background: form.tipo === t.value ? '#E1F5EE' : 'white', transition: 'all .15s' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: form.tipo === t.value ? '#0F6E56' : '#333' }}>{t.label}</div>
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>{t.descricao}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Nome do Alerta *</label>
                <input type="text" value={form.nome} onChange={e => setForm(prev => ({ ...prev, nome: e.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Veículo (deixe vazio para todos)</label>
                <select value={form.target_id} onChange={e => setForm(prev => ({ ...prev, target_id: e.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', background: 'white' }}>
                  <option value="">Todos os veículos</option>
                  {targets.map(t => <option key={t.id} value={t.id}>{t.icone} {t.identificador} — {t.nome}</option>)}
                </select>
              </div>

              {form.tipo === 'velocidade' && (
                <div>
                  <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Velocidade Máxima (km/h)</label>
                  <input type="number" value={form.configuracao.velocidade_maxima}
                    onChange={e => setForm(prev => ({ ...prev, configuracao: { ...prev.configuracao, velocidade_maxima: parseInt(e.target.value) } }))}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
              )}

              {form.tipo === 'sem_sinal' && (
                <div>
                  <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Minutos sem sinal para alertar</label>
                  <input type="number" value={form.configuracao.minutos_sem_sinal}
                    onChange={e => setForm(prev => ({ ...prev, configuracao: { ...prev.configuracao, minutos_sem_sinal: parseInt(e.target.value) } }))}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
              )}

              {form.tipo === 'cerca_virtual' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Tipo de Cerca</label>
                    <select value={form.configuracao.tipo_cerca} onChange={e => setForm(prev => ({ ...prev, configuracao: { ...prev.configuracao, tipo_cerca: e.target.value } }))}
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', background: 'white' }}>
                      <option value="saida">Alertar ao sair da área</option>
                      <option value="entrada">Alertar ao entrar na área</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
                      📍 Clique no mapa para definir o centro da cerca
                    </label>
                    <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #ddd' }}>
                      <MapContainer
                        center={[form.configuracao.lat || -23.3045, form.configuracao.lng || -51.1696]}
                        zoom={14}
                        style={{ height: '260px', width: '100%' }}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                        <MapaClicavel onClique={(lat, lng) => setForm(prev => ({ ...prev, configuracao: { ...prev.configuracao, lat, lng } }))} />
                        {form.configuracao.lat && form.configuracao.lng && (
                          <>
                            <Marker position={[form.configuracao.lat, form.configuracao.lng]} icon={iconeCerca} />
                            <Circle
                              center={[form.configuracao.lat, form.configuracao.lng]}
                              radius={form.configuracao.raio}
                              pathOptions={{ color: '#1D9E75', fillColor: '#1D9E75', fillOpacity: 0.15 }}
                            />
                          </>
                        )}
                      </MapContainer>
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                      <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Tamanho da área (raio)</label>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#1D9E75' }}>{formatarRaio(form.configuracao.raio)}</span>
                    </div>
                    <input
                      type="range"
                      min="100"
                      max="5000"
                      step="50"
                      value={form.configuracao.raio}
                      onChange={e => setForm(prev => ({ ...prev, configuracao: { ...prev.configuracao, raio: parseInt(e.target.value) } }))}
                      style={{ width: '100%', accentColor: '#1D9E75' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#999' }}>
                      <span>100 m</span>
                      <span>5 km</span>
                    </div>
                  </div>

                  <div style={{ fontSize: '12px', color: '#999' }}>💡 Toque no mapa para mover o centro da cerca, e arraste a barra para ajustar o tamanho</div>
                </div>
              )}

              <div style={{ background: '#f8f8f8', borderRadius: '10px', padding: '14px' }}>
                <div style={{ fontSize: '12px', color: '#666', fontWeight: '600', marginBottom: '10px' }}>NOTIFICAÇÕES</div>
                {[
                  { key: 'notificar_email', label: '📧 Notificar por Email' },
                  { key: 'notificar_push', label: '🔔 Notificar no painel (tempo real)' },
                ].map(f => (
                  <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#333', marginBottom: '8px' }}>
                    <input type="checkbox" checked={form[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.checked }))} />
                    {f.label}
                  </label>
                ))}
                {form.notificar_email && (
                  <input type="email" placeholder="Email para notificações" value={form.email_destino}
                    onChange={e => setForm(prev => ({ ...prev, email_destino: e.target.value }))}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box', marginTop: '8px' }} />
                )}
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '12px', position: 'sticky', bottom: 0, background: 'white' }}>
              <button onClick={() => setModalAberto(false)} style={{ background: '#f0f0f0', color: '#666', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600' }}>Cancelar</button>
              <button onClick={salvar} disabled={salvando} style={{ background: salvando ? '#ccc' : '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 24px', cursor: salvando ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
                {salvando ? '⏳ Salvando...' : '✅ Criar Alerta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Alertas;